import { ComponentInternalInstance, getCurrentInstance } from '@vue/composition-api';
import i18n, { i18n_t } from '@/locales/i18n';
import { isAdminOrSuperAdmin, isTeamAdmin } from '@/renderer/scripts/utils/UserRole/getters';
import { can, optAuth } from '@/renderer/scripts/composition/authHandler';
import { SMH } from '@/renderer/scripts/libs/smh-sdk-class';
type ItemType = SMH.SpaceFileItemData | SMH.FavoriteFileItemData | SMH.RelatedToMeFileItemData | any;
type ItemsType = Array<ItemType>;
/**
 * action过滤器
 * 需要留意：
 * 1. 过滤器的作用只会禁止权限，并不会开启权限; 也就意味着，每次经过过滤器，权限都会被禁用的越来越少
 * 2. 组合器的作用只会添加权限，并不会禁止权限；也就意味着组合器会逐个根据属性或配置添加权限
 *
 * 优先级： 组合器 < 过滤器，也就是说先执行组合器来进行权限的拼接，再根据不同场景来禁止掉某些权限
 *
 */
export const actionHandler = () => {
  const vm = (getCurrentInstance() as ComponentInternalInstance).proxy;

  /**
   * 动作-文案 映射表
   * @description
   *  在点击操作中出现的所有动作，需要留意的是这里只是常量的声明，不负责权限控制
   *  actionMap 是无序的，具体的顺序取决于 actionPool
   */
  const actionPool: Record<string, string> = {
    // 腾讯会议中此2入口放最上
    invite_to_preview: i18n_t('邀请预览'),
    invite_to_edit: i18n_t('邀请编辑'),
    detail: i18n_t('详情'),
    download: i18n_t('下载'),
    share: i18n_t('分享'),
    preview: i18n_t('在线预览'),
    edit: i18n_t('在线编辑'),
    esign: i18n_t('发起电子签'),
    rename: i18n_t('重命名'),
    addFavorite: i18n_t('收藏'),
    delFavorite: i18n_t('取消收藏'),
    fileCollect: i18n_t('发起文件收集'),
    requestPermission: i18n_t('权限申请'),
    auth: i18n_t('添加共享成员'),
    delete: i18n_t('删除'),
    move: i18n_t('移动到'),
    copy: i18n_t('复制到'),
    sync: i18n_t('同步文件'),
    history: i18n_t('历史版本'),
  };
  // action的组合器
  const comboMethods = {
    // actionByItem(item: any): Record<string, boolean> {},
    // actionByItems(item: Array<any>): Record<string, boolean> {},
    /**
     * 共享权限
     * @param actionMap
     */
    // shareCombo(actionMap: Record<string, boolean>): Record<string, boolean> {
    //   return actionMap;
    // },
    /**
     * 获取文件（夹）的权限
     * @description
     * 1. item不一定是根据selectedList[0]来进行取值，由调用方自行处理
     * 2. item大于一个后，会认定为多选模式
     * @param items
     * @param isContextMenu
     */
    getFileOrDirPermission(items: ItemsType, isContextMenu?: boolean): Record<string, boolean> {
      const { isMobileStyle, isMobile, isInWeMeetApp, enableShare, isPrivate } = vm.$store.getters;
      let actionMap = {};
      if (items.length === 1) {
        const item = items[0];
        actionMap = {};
        const canRequestPermission = true;
        const canShare = can('Share', item) && enableShare && item.visible;
        const dir = item.type === 'dir';
        const canPreview = !dir && optAuth('Preview', item);
        const canEdit = !dir && optAuth('Edit', item);
        const canFileCollect = dir && can('Upload', item) && item.spaceTag !== 'group';
        actionMap = {
          requestPermission: canRequestPermission,
          delete: can('Delete', item) && item.visible,
          rename: (can('Modify', item) || (can('Delete', item) && can('Download', item))) && item.visible,
          // 仅企业版有添加共享成员入口
          auth: can('Authorize', item) && item.visible,
          addFavorite: !item.favorited,
          delFavorite: item.favorited,
          preview: canPreview && item.visible,
          edit: canEdit && item.visible,
          esign: item.spaceTag === 'team' && optAuth('Esign', item) && item.visible && !isPrivate,
          // 非移动端界面不给 详情、历史版本、下载入口
          download:
            optAuth('Download', item) &&
            item.visible &&
            ((!isInWeMeetApp && isMobileStyle) || (isInWeMeetApp && !isMobile)),
          detail: isMobileStyle && can('View', item) && item.visible,
          history: isMobileStyle && !dir && item.visible,
          // 腾讯会议中才展示
          invite_to_preview: isInWeMeetApp && canShare && canPreview,
          invite_to_edit: isInWeMeetApp && canShare && canEdit,
          share: canShare,
          fileCollect: canFileCollect,
        };
      }
      //  TODO 目前暂时不抽取多选的函数
      if (items.length > 1) {
        actionMap = {};
      }
      return actionMap;
    },
  };

  // action的过滤器
  const filterMethods = {
    filterAll(actionMap: Record<string, boolean>, item?: ItemType): Record<string, boolean> {
      let action = actionMap;
      //  分享功能过滤器
      action = filterMethods.shareActionFilter(action);
      //  功能过滤器
      action = filterMethods.requestPermissionFilterByItem(action, item);
      //  路由过滤器
      action = filterMethods.routerFilter(action);
      //  套餐过滤器
      action = filterMethods.licenseFilter(action);
      return action;
    },
    /**
     * 有些操作不支持多选，在发现选中文件大于1个的时候，需要过滤掉一些权限
     * @param actionMap
     */
    multiFilter(actionMap: Record<string, boolean>): Record<string, boolean> {
      const keys = Object.keys(actionMap);
      keys.forEach((key: string) => {
        if (
          key !== 'share' &&
          key !== 'move' &&
          key !== 'copy' &&
          key !== 'delete' &&
          key !== 'requestPermission' &&
          key !== 'download'
        ) {
          actionMap[key] = false;
        }
      });
      return actionMap;
    },
    /**
     * 企业套餐过滤器
     * 发现不是企业的话，就过滤为false
     * @param actionMap
     */
    licenseFilter(actionMap: Record<string, boolean>): Record<string, boolean> {
      const { editionFlag } = vm.$store.getters;
      if (editionFlag !== 'enterprise') {
        actionMap.auth = false;
      }
      return actionMap;
    },
    /**
     * 某一些路由界面，不能出现对应操作
     * @param actionMap 权限控制参数
     * @description
     *
     */
    routerFilter(actionMap: Record<string, boolean>): Record<string, boolean> {
      const { sameEnterprise, groupAdmin } = vm.$store.getters;
      // 与我相关列表不能出现 移动和复制（因为与我相关的列表页文件有可能是别人的，只是因为分享被看到了）
      //  与我相关中不能够取消收藏 TODO 移动来的代码，逻辑似乎不太对
      if (vm.$route.name === 'relatedToMeFileList') {
        actionMap.move = false;
        actionMap.copy = false;
        actionMap.delFavorite = false;
        actionMap.addFavorite = false;
      }
      //  teamSpace之外页面 不允许电子签
      if (vm.$route.name !== 'teamSpace') {
        actionMap.esign = false;
      }
      // 若文件在个人空间
      if (vm.$route.name === 'personalSpace') {
        actionMap.requestPermission = false;
      }
      // 企业空间，我的收藏二级页面(企业内容）
      // 注意，收藏的二级页面群组文件会跳转到群组的路由，而个人空间/企业空间的文件才会进入二级页面
      const favoriteListInTeam = vm.$route.name === 'favoriteList' && vm.$route.query?.spaceTag === 'team';
      // 注意，与我相关的二级页面群组文件会跳转到群组的路由，而个人空间/企业空间的文件才会进入二级页面
      const relateTomeFileListInTeam = vm.$route.name === 'relatedToMeFileList' && vm.$route.query?.spaceTag === 'team';
      if (vm.$route.name === 'teamSpace' || favoriteListInTeam || relateTomeFileListInTeam) {
        // 发现是企业超管、管理员,则禁用权限申请
        // 发现是企业管理员,则禁用权限申请
        if (isTeamAdmin.value || isAdminOrSuperAdmin.value) {
          actionMap.requestPermission = false;
        }
      }
      // 协作群组中 发现是群主,则禁用权限申请
      if (['collaborationSpaceList'].includes(vm.$route.name || '') && groupAdmin) {
        actionMap.requestPermission = false;
      }
      //  禁止跨企业共享(sameEnterprise只有在当前路由'collaborationSpaceList'才有效)
      if ('collaborationSpaceList' === vm.$route.name && sameEnterprise === false) {
        actionMap.requestPermission = false;
      }
      // 协作群组 禁止共享授权
      if (['collaborationSpaceList', 'collaborationSpace'].includes(vm.$route.name || '')) {
        actionMap.auth = false;
      }
      return actionMap;
    },
    /**
     * 因为分享功能存在全局开关。
     * 若全局开关禁止分享，则全部禁用分享功能
     * @param actionMap
     */
    shareActionFilter(actionMap: Record<string, boolean>): Record<string, boolean> {
      const { enableShare } = vm.$store.getters;
      if (enableShare === false) {
        actionMap.share = false;
      }
      return actionMap;
    },
    /**
     * 文件审批权限过滤器
     * 1. 因超管和团队管理员不存在文件审批功能，所以禁用。
     * 2. 来自个人空间（自己的个人空间的）需要禁用
     * 3. 来自其他企业（不同的orgId）的文件，需要禁用
     * @param actionMap
     * @param item
     */
    requestPermissionFilterByItem(actionMap: Record<string, boolean>, item?: ItemType): Record<string, boolean> {
      const { sameEnterprise, groupAdmin, userId, orgId: userOrgId, userBaseInfo } = vm.$store.getters;
      // 来自协作群组（其他企业）的文件需要禁用，禁止跨团队分享
      if (item?.spaceOrgId !== undefined && !isNaN(+item?.spaceOrgId) && +item?.spaceOrgId !== userOrgId) {
        actionMap.requestPermission = false;
      }
      if (item?.canApply === false && item?.hasApplied === false) {
        actionMap.requestPermission = false;
      }
      switch (item?.spaceTag) {
        case 'team':
          // 若是超管/管理员(相同企业）则禁用
          if (isAdminOrSuperAdmin.value && userOrgId === item?.team?.orgId) {
            actionMap.requestPermission = false;
          }
          // 若是团队管理员，则禁用
          if (userBaseInfo?.canManageTeams?.includes(item?.team?.id)) {
            actionMap.requestPermission = false;
          }
          break;
        case 'group':
          // 若是群主，则禁用
          if (item?.group?.userId === userId && item?.group?.orgId === userOrgId) {
            actionMap.requestPermission = false;
          }
          break;
        case 'personal':
          // 若文件来自自己的个人空间，则禁用
          if (item?.user?.userId === userId && item?.user?.orgId === userOrgId) {
            actionMap.requestPermission = false;
          }
          break;
        default:
          break;
      }
      return actionMap;
    },
  };

  // action的一些处理逻辑
  const actionMethods = {
    calcActionRecord() {
      // 内部缓存action
      let intersectionMapCache: Record<string, boolean> = {};
      // 计算并集： {1,2,3}∩{2,3,4}={2,3}
      const intersection = (actionMap: Record<string, boolean>, index: number) => {
        const keys = Object.keys(actionMap);
        keys.forEach((key: string) => {
          const actionMapElement = actionMap[key] || false;
          const cacheElement = intersectionMapCache[key];
          // 第一次进入的时候，cache没有值，则直接使用actionMap的值
          const intersectionMapCacheElement = cacheElement === undefined ? actionMapElement : cacheElement;
          intersectionMapCache[key] = actionMapElement && intersectionMapCacheElement;
        });
        return intersectionMapCache;
      };
      // 清理计算缓存
      const clear = () => {
        intersectionMapCache = {};
      };
      return {
        intersection,
        clear,
      };
    },
    /**
     * 根据actionMap来计算得到options
     * @return options {Array<{ content: string; value: string }>}
     */
    calcOptionsByActionMap(actionMap: Record<string, boolean>): Array<{
      content: string;
      value: string;
    }> {
      const options: Array<{
        content: string;
        value: string;
      }> = [];
      Object.keys(actionPool).forEach((key: string) => {
        if (actionMap[key]) {
          const element = actionPool[key];
          options.push({
            content: element,
            value: key,
          });
        }
      });
      return options;
    },
  };
  return {
    actionPool,
    filterMethods,
    comboMethods,
    actionMethods,
  };
};
